import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, DollarSign, TrendingUp, Package, MapPin, Maximize2, RefreshCw, X, Plus } from 'lucide-react';
import { getLoanById, getPledgesByLoan, createPayment, getPaymentsByLoan, createLoanAdvance } from '../services/api';
import { calculatePledgeInterest } from '../utils/pledgeInterest';
import {
  calculateLoanDueAmount,
} from '../utils/loanInterest';
import Toast from '../components/Toast';
import { useLanguage } from '../contexts/LanguageContext';

const createIdempotencyKey = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const LoanDetails = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const texts = t('loanDetails');
  const pledgeTexts = t('pledgeDetails');
  const naLabel = t('loans.labels.na');
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showImageModal, setShowImageModal] = useState(false);
  const [pledges, setPledges] = useState([]);
  const [loadingPledges, setLoadingPledges] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'CASH',
    paymentType: 'REGULAR_PAYMENT',
    description: '',
    referenceNumber: '',
    idempotencyKey: ''
  });
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({
    amount: '',
    advanceDate: new Date().toISOString().slice(0, 10),
    idempotencyKey: ''
  });
  const [isSubmittingAdvance, setIsSubmittingAdvance] = useState(false);
  
  // Use refs to track if API calls are in progress and prevent duplicates
  const loadingRef = useRef(false);
  const pledgesLoadingRef = useRef(false);
  const paymentsLoadingRef = useRef(false);

  const loanDueSummary = useCallback(() => {
    if (!loan) {
      return {
        principal: 0,
        monthlyInterest: 0,
        monthsElapsed: 1,
        accruedInterest: 0,
        totalDue: 0,
      };
    }
    return calculateLoanDueAmount(loan);
  }, [loan]);

  const calculateMonthsElapsed = useCallback(() => loanDueSummary().monthsElapsed, [loanDueSummary]);

  const calculateLoanDuration = (startDate) => {
    if (!startDate) return naLabel;
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = Math.abs(today - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;

    const monthLabel = months === 1 ? texts.durationSingleUnit : texts.durationUnit;
    const daySingular = language === 'hi' ? 'दिन' : 'day';
    const dayPlural = language === 'hi' ? 'दिन' : 'days';
    const dayLabel = days === 1 ? daySingular : dayPlural;

    return `${months} ${monthLabel}, ${days} ${dayLabel}`;
  };

  const calculateTotalDue = useCallback(() => loanDueSummary().totalDue, [loanDueSummary]);

  const dueSummary = loan?.dueSummary;
  const calculatedSummary = loanDueSummary();
  const displayedAccruedInterest = dueSummary?.accruedInterest ?? calculatedSummary.accruedInterest;
  const displayedMonthlyInterest = calculatedSummary.monthlyInterest;
  const displayedNormalInterest = dueSummary?.accruedInterest != null
    ? Math.max(0, calculatedSummary.normalInterest)
    : calculatedSummary.normalInterest;
  const displayedOverdueInterest = dueSummary?.accruedInterest != null
    ? Math.max(0, displayedAccruedInterest - displayedNormalInterest)
    : calculatedSummary.overdueInterest;
  const totalDueAmount = dueSummary?.totalDue ?? calculateTotalDue();
  const totalPaidAmount = dueSummary?.totalPaid ?? payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalRoundOffAmount = dueSummary?.totalRoundOff ?? payments.reduce(
    (sum, p) => sum + parseFloat(p.roundOfAmount || p.roundOffAmount || 0), 0
  );
  const outstandingBalanceBeforePayment = dueSummary?.outstandingBalance
    ?? Math.max(0, totalDueAmount - totalPaidAmount - totalRoundOffAmount);
  const outstandingPrincipalBeforePayment = dueSummary?.outstandingPrincipal
    ?? Math.max(0, parseFloat(loan?.principalAmount || loan?.totalAdvanced || loan?.finalDisbursedAmount || 0)
      - totalPaidAmount);
  const outstandingInterestBeforePayment = dueSummary?.outstandingInterest
    ?? Math.max(0, outstandingBalanceBeforePayment - outstandingPrincipalBeforePayment);
  const currentTotalAdvanced = parseFloat(loan?.totalAdvanced ?? loan?.finalDisbursedAmount ?? 0) || 0;
  const maximumEligibleAmount = parseFloat(loan?.maximumEligibleAmount || 0) || 0;
  const remainingEligibleAmount = Math.max(
    0,
    maximumEligibleAmount - currentTotalAdvanced - displayedAccruedInterest
  );
  const requestedAdvanceAmount = parseFloat(advanceForm.amount || '0') || 0;
  const advanceLimitExceeded = requestedAdvanceAmount > remainingEligibleAmount;
  const rawPaymentAmountInput = parseFloat(paymentForm.amount || '0');
  const paymentAmountValue = Number.isFinite(rawPaymentAmountInput) ? rawPaymentAmountInput : 0;
  const roundOffAmountValue = paymentForm.paymentType === 'FULL_SETTLEMENT' && paymentAmountValue > 0
    ? Math.max(0, Number((outstandingBalanceBeforePayment - paymentAmountValue).toFixed(2)))
    : 0;
  const paymentMaximum = paymentForm.paymentType === 'INTEREST_ONLY'
    ? outstandingInterestBeforePayment
    : outstandingBalanceBeforePayment;
  const allocationPreview = (() => {
    const cash = Math.max(0, paymentAmountValue);
    if (paymentForm.paymentType === 'INTEREST_ONLY') {
      return { interest: Math.min(cash, outstandingInterestBeforePayment), principal: 0, roundOff: 0 };
    }
    const interest = Math.min(cash, outstandingInterestBeforePayment);
    const principal = Math.min(Math.max(cash - interest, 0), outstandingPrincipalBeforePayment);
    return { interest, principal, roundOff: roundOffAmountValue };
  })();

  const loadLoanDetails = useCallback(async () => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    try {
      if (location.state?.loan) {
        setLoan(location.state.loan);
      }

      const result = await getLoanById(loanId);
      if (result.success && result.data) {
        setLoan(result.data);
      } else {
        setToast({ show: true, message: result.message || texts.notFound, type: 'error' });
        setTimeout(() => navigate('/loans'), 2000);
      }
    } catch (error) {
      console.error('Error loading loan:', error);
      setToast({ show: true, message: texts.messages.loadError, type: 'error' });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId, location.state, navigate]);

  const fetchPledges = useCallback(async () => {
    // Prevent duplicate calls
    if (pledgesLoadingRef.current || !loanId) return;
    
    pledgesLoadingRef.current = true;
    try {
      setLoadingPledges(true);
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId || !loanId) {
        setLoadingPledges(false);
        pledgesLoadingRef.current = false;
        return;
      }

      const result = await getPledgesByLoan(businessId, loanId);
      
      if (result.success && result.data) {
        setPledges(result.data);
      }
    } catch (err) {
      console.error('Error fetching pledges:', err);
    } finally {
      setLoadingPledges(false);
      pledgesLoadingRef.current = false;
    }
  }, [loanId]);

  useEffect(() => {
    loadLoanDetails();
  }, [loadLoanDetails]);

  // Fetch payments for the loan
  const fetchPayments = useCallback(async () => {
    // Prevent duplicate calls
    if (paymentsLoadingRef.current || !loanId) return;
    
    paymentsLoadingRef.current = true;
    try {
      setLoadingPayments(true);
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId || !loanId) {
        setLoadingPayments(false);
        paymentsLoadingRef.current = false;
        return;
      }

      const result = await getPaymentsByLoan(businessId, loanId);
      
      if (result.success && result.data) {
        setPayments(Array.isArray(result.data) ? result.data : []);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoadingPayments(false);
      paymentsLoadingRef.current = false;
    }
  }, [loanId]);

  useEffect(() => {
    if (loan && loan.id) {
      fetchPledges();
      fetchPayments();
    }
  }, [loan, fetchPledges, fetchPayments]);

  const formatBorrowerName = () => {
    return loan?.name || loan?.borrowerName || naLabel;
  };

  const getStatusStyle = (status) => {
    const styles = {
      'ACTIVE': { background: '#d1fae5', color: '#065f46' },
      'AWAITED': { background: '#fef3c7', color: '#92400e' },
      'OVERDUE': { background: '#fee2e2', color: '#991b1b' },
      'CLOSED': { background: '#f3f4f6', color: '#374151' },
      'COMPLETED': { background: '#dbeafe', color: '#1e40af' }
    };
    return styles[status] || styles['AWAITED'];
  };

  const handleRecordPayment = () => {
    setShowPaymentModal(true);
    // Reset form
    setPaymentForm({
      amount: '',
      paymentMethod: 'CASH',
      paymentType: 'REGULAR_PAYMENT',
      description: '',
      referenceNumber: '',
      idempotencyKey: ''
    });
  };

  const handleAdditionalLoan = () => {
    setAdvanceForm({ amount: '', advanceDate: new Date().toISOString().slice(0, 10), idempotencyKey: '' });
    setShowAdvanceModal(true);
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
    setToast({ show: true, message: texts.messages.paymentAmountInvalid, type: 'error' });
      return;
    }

    setIsSubmittingPayment(true);
    try {
      const idempotencyKey = paymentForm.idempotencyKey || createIdempotencyKey();
      setPaymentForm(prev => ({ ...prev, idempotencyKey }));
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId || !loan) {
      setToast({ show: true, message: texts.messages.missingBusiness, type: 'error' });
        setIsSubmittingPayment(false);
        return;
      }

      const paymentData = {
        businessId: businessId,
        borrowerId: loan.borrowerId,
        loanId: loan.id,
        amount: parseFloat(paymentForm.amount),
        paymentType: paymentForm.paymentType,
        idempotencyKey,
        roundOfAmount: roundOffAmountValue,
        paymentMethod: paymentForm.paymentMethod,
        description: paymentForm.description,
        ...(paymentForm.referenceNumber ? { referenceNumber: paymentForm.referenceNumber } : {})
      };

      const result = await createPayment(paymentData);
      
      if (result.success) {
        setShowPaymentModal(false);
        setToast({ show: true, message: texts.messages.paymentCreateSuccess, type: 'success' });
        // Refresh payments list
        await fetchPayments();
        await loadLoanDetails();
        // Reset form
        setPaymentForm({
          amount: '',
          paymentMethod: 'CASH',
          paymentType: 'REGULAR_PAYMENT',
          description: '',
          referenceNumber: '',
          idempotencyKey: ''
        });
      } else {
        setToast({ show: true, message: result.message || texts.messages.paymentCreateError, type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      setToast({ show: true, message: texts.messages.paymentCreateError, type: 'error' });
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentForm({
      amount: '',
      paymentMethod: 'CASH',
      paymentType: 'REGULAR_PAYMENT',
      description: '',
      referenceNumber: '',
      idempotencyKey: ''
    });
  };

  const handleAdvanceFormChange = (e) => {
    const { name, value } = e.target;
    setAdvanceForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitAdvance = async (e) => {
    e.preventDefault();
    if (!advanceForm.amount || parseFloat(advanceForm.amount) <= 0) {
      setToast({ show: true, message: texts.messages?.advanceAmountInvalid || 'Advance amount must be greater than zero', type: 'error' });
      return;
    }
    if (remainingEligibleAmount <= 0 || advanceLimitExceeded) {
      setToast({
        show: true,
        message: `Additional loan cannot exceed remaining eligible amount of ₹${remainingEligibleAmount.toLocaleString('en-IN')}`,
        type: 'error'
      });
      return;
    }
    setIsSubmittingAdvance(true);
    try {
      const idempotencyKey = advanceForm.idempotencyKey || createIdempotencyKey();
      setAdvanceForm(prev => ({ ...prev, idempotencyKey }));
      const result = await createLoanAdvance(loan.id, {
        amount: parseFloat(advanceForm.amount),
        advanceDate: advanceForm.advanceDate || undefined,
        idempotencyKey
      });
      if (result.success) {
        setShowAdvanceModal(false);
        setToast({ show: true, message: texts.messages?.advanceCreateSuccess || 'Additional loan added successfully', type: 'success' });
        await loadLoanDetails();
        await fetchPayments();
        setAdvanceForm({ amount: '', advanceDate: new Date().toISOString().slice(0, 10), idempotencyKey: '' });
      } else {
        setToast({ show: true, message: result.message || texts.messages?.advanceCreateError || 'Unable to add additional loan', type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting loan advance:', error);
      setToast({ show: true, message: texts.messages?.advanceCreateError || 'Unable to add additional loan', type: 'error' });
    } finally {
      setIsSubmittingAdvance(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>{texts.loading}</p>
        </div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)' }}>{texts.notFound}</p>
          <button 
            onClick={() => navigate('/loans')}
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            {texts.backToList}
          </button>
        </div>
      </div>
    );
  }

  // The ledger is authoritative backend data. It includes real disbursements,
  // payments, and separately recorded settlement adjustments.
  const transactions = (loan.transactions || []).map((transaction) => {
    const transactionDate = transaction.transactionDate || transaction.createdAt;
    const type = transaction.transactionType || 'PAYMENT';
    return {
      ...transaction,
      date: transactionDate
        ? new Date(transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : naLabel,
      description: transaction.description || type.replaceAll('_', ' '),
      paymentMethod: transaction.paymentMethod || '-',
      referenceNumber: transaction.referenceNumber || '',
      amount: parseFloat(transaction.amount || 0),
      roundOffAmount: type === 'ROUND_OFF' ? parseFloat(transaction.amount || 0) : 0,
      isRoundOff: type === 'ROUND_OFF',
      isDisbursement: type === 'INITIAL_DISBURSEMENT' || type === 'ADDITIONAL_DISBURSEMENT',
    };
  }).sort((a, b) => new Date(b.transactionDate || 0) - new Date(a.transactionDate || 0));
  const statusStyle = getStatusStyle(loan.status);

  // Calculate pledge details from API data using new interest calculation
  const calculatePledgeDetails = () => {
    if (!loan || !(loan.totalAdvanced || loan.finalDisbursedAmount) || pledges.length === 0) return null;
    
    // Get the first active pledge (or latest one)
    const pledge = pledges.find(p => p.isActive) || pledges[0];
    if (!pledge) return null;
    
    const loanAmount = parseFloat(loan.totalAdvanced || loan.finalDisbursedAmount || 0);
    const pledgeAmount = parseFloat(pledge.pledgeAmount || 0);
    const pledgeRate = parseFloat(pledge.interestRate || 0);
    const loanRate = parseFloat(loan.interestRatePerMonth || 0);
    
    const endDate = pledge.interestFreezeDate
      || (pledge.collateralStatus === 'HANDOVER' || pledge.collateralStatus === 'IN_HAND' || pledge.isActive === false
        ? (pledge.updatedAt || pledge.maturityDate)
        : null);
    
    const interestBreakdown = calculatePledgeInterest(
      pledgeAmount,
      pledgeRate,
      pledge.pledgeDate,
      endDate
    );
    
    // Monthly interests for net profit calculation
    const monthlyLoanInterest = (loanAmount * loanRate) / 100;
    const monthlyPledgeInterest = (pledgeAmount * pledgeRate) / 100;
    
    // Net profit per month
    const netMonthlyProfit = Math.round(monthlyLoanInterest - monthlyPledgeInterest);
    
    return {
      pledgedTo: pledge.pledgedTo,
      pledgeAmount,
      pledgeRate,
      accruedPledgeInterest: interestBreakdown.totalInterest,
      interestBreakdown,
      duration: calculateLoanDuration(pledge.pledgeDate),
      netProfit: netMonthlyProfit,
      documentUrl: pledge.documentUrl
    };
  };

  const pledgeDetails = calculatePledgeDetails();

  return (
    <div>
      {toast.show && toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <div>
        {/* Header */}
        <div style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={() => navigate('/loans')}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
            >
              <ArrowLeft size={20} />
              {texts.back}
            </button>
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                {t('loans.loanId')}: {loan.loanNumber || t('loans.labels.na')}
              </p>
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                margin: '0.25rem 0 0'
              }}>
                {texts.serialNumber || 'Serial Number'}:{' '}
                <span style={{
                  fontWeight: 700,
                  color: loan.serialNumber ? '#1d4ed8' : '#9ca3af',
                  fontStyle: loan.serialNumber ? 'normal' : 'italic',
                  textTransform: loan.serialNumber ? 'uppercase' : 'none'
                }}>
                  {loan.serialNumber || t('loans.labels.serialMissing') || '-'}
                </span>
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: '500',
              background: statusStyle.background,
              color: statusStyle.color
            }}>
              {loan.status}
            </span>
            <button
              onClick={handleRecordPayment}
              className="btn btn-primary"
            >
              {texts.recordPayment}
            </button>
            <button
              onClick={handleAdditionalLoan}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              disabled={loan.status !== 'ACTIVE'}
            >
              <Plus size={17} />
              {texts.additionalLoan || 'Additional Loan'}
            </button>
          </div>
        </div>

        {/* Loan Summary Card */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h2 className="card-title">{texts.summaryTitle}</h2>
          </div>
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem'
            }}>
              {/* Borrower Info */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#dbeafe',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Package style={{ color: '#2563eb' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.borrowerLabel}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {formatBorrowerName()}
                  </div>
                </div>
              </div>

              {/* Principal */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#d1fae5',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DollarSign style={{ color: '#059669' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.principalLabel}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    ₹{parseFloat(loan.totalAdvanced ?? loan.finalDisbursedAmount ?? 0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Accrued Interest */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#fed7aa',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrendingUp style={{ color: '#ea580c' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestLabel}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    ₹{displayedAccruedInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#e9d5ff',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Calendar style={{ color: '#9333ea' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.durationLabel}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {loan.tenureMonths || 0} {loan.tenureMonths === 1 ? texts.durationSingleUnit : texts.durationUnit}
                  </div>
                </div>
              </div>
            </div>

            {/* Interest Breakdown and Total Due */}
            <div style={{
              paddingTop: '1rem',
              marginTop: '1rem',
              borderTop: '1px solid var(--border-color)'
            }}>
              {/* Interest Breakdown */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid var(--border-color)'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {texts.interestRow} ({calculateMonthsElapsed()} {calculateMonthsElapsed() === 1 ? texts.durationSingleUnit : texts.durationUnit} @ {loan.interestRatePerMonth || 0}% {texts.interestPerMonth})
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    ₹{displayedMonthlyInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}/month current block
                    {' · '}Normal ₹{displayedNormalInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    {' · '}Overdue ₹{displayedOverdueInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
                <span style={{ fontSize: '1rem', fontWeight: '600', color: '#ea580c' }}>
                  ₹{displayedAccruedInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>

              {/* Principal + Interest = Total */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid var(--border-color)'
              }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {texts.principalLabel}
                </span>
                <span style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                  ₹{parseFloat(loan.totalAdvanced ?? loan.finalDisbursedAmount ?? 0).toLocaleString('en-IN')}
                </span>
              </div>

              {/* Total paid */}
              {totalPaidAmount > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {texts.totalPaid}
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: '#059669' }}>
                    − ₹{totalPaidAmount.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Round-off waived (not counted as cash received) */}
              {totalRoundOffAmount > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {texts.paymentModal?.roundOffAmount || 'Round-off (waived)'}
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: '#2563eb' }}>
                    − ₹{totalRoundOffAmount.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '0.75rem',
                marginBottom: '0.75rem'
              }}>
                <div style={{ padding: '0.75rem', background: 'var(--bg-muted)', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{texts.outstandingPrincipal || 'Outstanding principal'}</div>
                  <div style={{ fontWeight: 700, color: '#b45309', marginTop: '0.25rem' }}>
                    ₹{outstandingPrincipalBeforePayment.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', background: 'var(--bg-muted)', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{texts.outstandingInterest || 'Outstanding interest'}</div>
                  <div style={{ fontWeight: 700, color: '#ea580c', marginTop: '0.25rem' }}>
                    ₹{outstandingInterestBeforePayment.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Total Amount Due */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '0.75rem',
                borderTop: '2px solid var(--border-color)'
              }}>
                <span style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {texts.totalDue}
                </span>
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc2626' }}>
                  ₹{outstandingBalanceBeforePayment.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Advance Ledger */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">{texts.advanceLedger || 'Loan Advances'}</h2>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {texts.maximumEligibleAmount || 'Maximum eligible'}: ₹{parseFloat(loan.maximumEligibleAmount || 0).toLocaleString('en-IN')}
            </span>
          </div>
          <div style={{ padding: '0 1.5rem 1rem' }}>
            {(loan.advances || []).length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{texts.advanceDate || 'Advance date'}</th>
                      <th>{texts.advanceAmount || 'Amount'}</th>
                      <th>{texts.interestLabel || 'Interest'}</th>
                      <th>{texts.outstandingPrincipal || 'Outstanding principal'}</th>
                      <th>{texts.outstandingInterest || 'Outstanding interest'}</th>
                      <th>{texts.status || 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.advances.map((advance, index) => (
                      <tr key={advance.id || `${advance.advanceDate}-${index}`}>
                        <td>{advance.advanceDate || naLabel}</td>
                        <td>₹{parseFloat(advance.amount || 0).toLocaleString('en-IN')}</td>
                        <td>₹{parseFloat(advance.accruedInterest || 0).toLocaleString('en-IN')}</td>
                        <td>₹{parseFloat(advance.outstandingPrincipal || 0).toLocaleString('en-IN')}</td>
                        <td>₹{parseFloat(advance.outstandingInterest || 0).toLocaleString('en-IN')}</td>
                        <td>{advance.status || naLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '1.5rem 0', color: 'var(--text-secondary)' }}>
                {texts.noAdvances || 'No advance records found'}
              </div>
            )}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="detail-layout-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gap: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Re-pledging Details */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">{texts.pledgeCardTitle}</h2>
              </div>
              <div>
                {loadingPledges ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '3rem' }}>
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)' }} />
                    <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{t('collateral.loadingMessage')}</span>
                  </div>
                ) : pledgeDetails ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {/* Pledge Summary */}
                    <div style={{
                      background: 'rgba(37, 99, 235, 0.12)',
                      border: '1px solid var(--border-color)',
                      padding: '1.25rem',
                      borderRadius: '0.5rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.pledgedTo}</span>
                        <span style={{ fontWeight: '600', fontSize: '0.9375rem', color: 'var(--text-primary)' }}>{pledgeDetails.pledgedTo || naLabel}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.pledgedAmount}</span>
                        <span style={{ fontWeight: 'bold', fontSize: '1.25rem', color: '#3b82f6' }}>
                          ₹{pledgeDetails.pledgeAmount.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>

                    {/* Pledge Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem 0',
                        borderBottom: '1px solid var(--border-color)'
                      }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{pledgeTexts.interestRate}</span>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{pledgeDetails.pledgeRate}% {texts.interestPerMonth}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem 0',
                        borderBottom: '1px solid var(--border-color)'
                      }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{pledgeTexts.interestEarned}</span>
                        <span style={{ fontWeight: '600', color: '#dc2626', fontSize: '0.875rem' }}>
                          ₹{pledgeDetails.accruedPledgeInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem 0',
                        borderBottom: '1px solid var(--border-color)'
                      }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{pledgeTexts.pledgeDuration}</span>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                          {pledgeDetails.interestBreakdown ?
                            `${pledgeDetails.interestBreakdown.months} ${pledgeDetails.interestBreakdown.months === 1 ? texts.durationSingleUnit : texts.durationUnit}${pledgeDetails.interestBreakdown.additionalDays > 0 ? `, ${pledgeDetails.interestBreakdown.additionalDays} ${pledgeTexts.additionalDaysLabel}` : ''}` :
                            pledgeDetails.duration
                          }
                        </span>
                      </div>
                      {pledgeDetails.interestBreakdown && pledgeDetails.interestBreakdown.additionalDays > 0 && (
                        <div style={{
                          background: 'var(--bg-muted)',
                          padding: '0.75rem',
                          borderRadius: '0.5rem',
                          border: '1px solid var(--border-color)',
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)'
                        }}>
                          <div style={{ marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
                            <strong>{pledgeTexts.interestBreakdownTitle}:</strong>
                          </div>
                          <div>• 1 {texts.durationSingleUnit}: ₹{pledgeDetails.interestBreakdown.oneMonthInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                          <div>• {pledgeDetails.interestBreakdown.additionalDays} {pledgeTexts.additionalDaysLabel}: ₹{pledgeDetails.interestBreakdown.additionalDaysInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                          <div style={{ marginTop: '0.25rem', fontWeight: '600', color: '#f87171' }}>
                            • Total: ₹{pledgeDetails.accruedPledgeInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Net Profit */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1.25rem',
                      background: 'rgba(5, 150, 105, 0.15)',
                      border: '1px solid rgba(5, 150, 105, 0.35)',
                      borderTop: '3px solid #059669',
                      borderRadius: '0.5rem'
                    }}>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{pledgeTexts.netProfit}</span>
                      <span style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#10b981' }}>
                        + ₹{pledgeDetails.netProfit.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
                    <p>{pledgeTexts.messages?.noRecords || t('collateral.emptyState')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Transaction Ledger */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">{texts.transactionLedger || 'Transaction Ledger (Khata)'}</h2>
              </div>
              {loadingPayments ? (
                <div style={{ padding: '3rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                  <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)' }} />
                  <span style={{ color: '#9ca3af' }}>{texts.loadingTransactions || 'Loading transactions...'}</span>
                </div>
              ) : transactions.length > 0 ? (
                <div className="table-container">
                  <table className="table ledger-table">
                    <thead>
                      <tr>
                        <th>{texts.ledgerHeaders?.date || 'DATE'}</th>
                        <th>{texts.ledgerHeaders?.description || 'DESCRIPTION'}</th>
                        <th>{texts.ledgerHeaders?.paymentMethod || 'PAYMENT METHOD'}</th>
                        <th>{texts.ledgerHeaders?.reference || 'REFERENCE'}</th>
                        <th>{texts.ledgerHeaders?.roundOff || 'ROUND-OFF'}</th>
                        <th style={{ textAlign: 'right' }}>{texts.ledgerHeaders?.amount || 'AMOUNT'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => (
                        <tr key={index}>
                          <td style={{ fontSize: '0.875rem' }}>{txn.date}</td>
                          <td style={{ fontSize: '0.875rem' }}>{txn.description}</td>
                          <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{txn.paymentMethod || '-'}</td>
                          <td
                            className={txn.referenceNumber ? 'ledger-ref-cell' : 'ledger-ref-cell ledger-ref-cell--empty'}
                            style={{ fontSize: '0.875rem', color: txn.referenceNumber ? '#374151' : '#6b7280' }}
                          >
                            {txn.referenceNumber || '-'}
                          </td>
                          <td style={{ fontSize: '0.875rem', color: txn.roundOffAmount ? '#2563eb' : '#6b7280' }}>
                            {txn.roundOffAmount
                              ? `₹${txn.roundOffAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '-'}
                          </td>
                          <td style={{
                            textAlign: 'right',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            color: txn.isRoundOff ? '#2563eb' : (txn.amount < 0 ? '#dc2626' : '#059669')
                          }}>
                            {txn.isRoundOff
                              ? `₹${Math.abs(txn.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })} (non-cash)`
                              : `${txn.amount < 0 ? '-' : '+'} ₹${Math.abs(txn.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
                  {texts.noTransactionsFound || 'No transactions found'}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Item Details with Image - Combined */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">{texts.itemDetails || 'Item Details'}</h2>
              </div>
              <div style={{ padding: '1rem 1.5rem' }}>
                {/* Item Details */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                      {loan.itemDescription || `${loan.metalType} Item`}
                    </h3>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--border-color)'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.metalType || 'Metal Type'}:</span>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{loan.metalType || naLabel}</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--border-color)'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.purity || 'Purity'}:</span>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{loan.purityKarat || naLabel}</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--border-color)'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.netWeight || 'Net Weight'}:</span>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      {loan.netWeightGrams ? `${loan.netWeightGrams}g` : naLabel}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--border-color)'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.serialNumber || 'Serial Number'}:</span>
                    <span style={{
                      fontWeight: '700',
                      fontSize: '0.875rem',
                      color: loan.serialNumber ? '#1d4ed8' : '#9ca3af',
                      fontStyle: loan.serialNumber ? 'normal' : 'italic',
                      textTransform: loan.serialNumber ? 'uppercase' : 'none'
                    }}>
                      {loan.serialNumber || t('loans.labels.serialMissing') || '-'}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--border-color)'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.lendingRate || 'Lending Rate per Gram'}:</span>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      ₹{loan.lendingRatePerGram || naLabel}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    paddingTop: '0.5rem'
                  }}>
                    <MapPin size={16} style={{ color: '#2563eb' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      {texts.collateralStatus || 'Collateral Status'}:{' '}
                      <span style={{ 
                        fontWeight: '600', 
                        color: loan.collateralStatus === 'IN_HAND' ? '#059669' : 
                               loan.collateralStatus === 'PLEDGED' ? '#ea580c' : 
                               loan.collateralStatus === 'AWAITING' ? '#92400e' : 
                               loan.collateralStatus === 'HANDOVER' ? '#3730a3' : '#6b7280'
                      }}>
                        {texts.collateralStatusValues?.[loan.collateralStatus] || loan.collateralStatus || naLabel}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Collateral Image */}
                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                    {texts.collateralImage || 'Collateral Image'}
                  </h4>
                  {loan.photosUrl && loan.photosUrl !== 'string' ? (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={loan.photosUrl}
                        alt="Collateral Item"
                        style={{
                          width: '100%',
                          height: '22rem',
                          objectFit: 'cover',
                          borderRadius: '0.5rem',
                          border: '1px solid var(--border-color)'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Maximize Button */}
                      <button
                        onClick={() => setShowImageModal(true)}
                        style={{
                          position: 'absolute',
                          top: '0.75rem',
                          right: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(0, 0, 0, 0.6)';
                        }}
                      >
                        <Maximize2 size={20} />
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '22rem',
                      background: 'var(--bg-muted)',
                      borderRadius: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No image available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {showImageModal && loan.photosUrl && loan.photosUrl !== 'string' && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '2rem'
            }}
            onClick={() => setShowImageModal(false)}
          >
            <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
              <img
                src={loan.photosUrl}
                alt="Collateral Item - Full View"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '0.5rem'
                }}
              />
              <button
                onClick={() => setShowImageModal(false)}
                style={{
                  position: 'absolute',
                  top: '-2.5rem',
                  right: '-2.5rem',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '2.5rem',
                  height: '2.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Additional Loan Modal */}
        {showAdvanceModal && (
          <div className="modal-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'var(--bg-surface)', borderRadius: '1rem', padding: '1.5rem',
              width: '520px', maxWidth: '95vw', border: '1px solid var(--border-color)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.35rem' }}>{texts.additionalLoan || 'Additional Loan'}</h2>
                <button type="button" onClick={() => setShowAdvanceModal(false)} style={{ background: 'none', border: 0, cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{ padding: '0.75rem', background: 'var(--bg-muted)', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
                {texts.currentTotalAdvanced || 'Current total advanced'}: ₹{currentTotalAdvanced.toLocaleString('en-IN')}<br />
                {texts.currentInterestRate || 'Current interest rate'}: {parseFloat(loan.interestRatePerMonth || 0).toLocaleString('en-IN')}% {texts.interestPerMonth || 'per month'}<br />
                Current accrued interest: ₹{displayedAccruedInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}<br />
                {texts.remainingEligibleAmount || 'Remaining eligible'}: ₹{remainingEligibleAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
              <form onSubmit={handleSubmitAdvance}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                      {texts.advanceAmount || 'Advance amount'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input type="number" name="amount" value={advanceForm.amount} onChange={handleAdvanceFormChange}
                      min="0.01" max={remainingEligibleAmount} step="0.01" required autoFocus
                      disabled={remainingEligibleAmount <= 0}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'var(--bg-surface)' }} />
                    {remainingEligibleAmount <= 0 ? (
                      <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.4rem' }}>
                        No eligible amount remains after including accrued interest.
                      </div>
                    ) : advanceLimitExceeded ? (
                      <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.4rem' }}>
                        Amount cannot exceed ₹{remainingEligibleAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}.
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                      {texts.advanceDate || 'Advance date'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input type="date" name="advanceDate" value={advanceForm.advanceDate} onChange={handleAdvanceFormChange}
                      max={new Date().toISOString().slice(0, 10)} required style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'var(--bg-surface)' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAdvanceModal(false)}>
                    {texts.paymentModal?.cancel || 'Cancel'}
                  </button>
                  <button type="submit" className="btn btn-primary"
                    disabled={isSubmittingAdvance || remainingEligibleAmount <= 0 || advanceLimitExceeded}>
                    {isSubmittingAdvance ? (texts.addingAdditionalLoan || 'Adding...') : (texts.saveAdditionalLoan || 'Add loan')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: '1rem',
              padding: '1.5rem',
              width: '750px',
              maxWidth: '95vw',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid var(--border-color)',
              position: 'relative'
            }}>
              {/* Modal Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                  margin: 0
                }}>
                  {texts.paymentModal?.recordPayment || 'Record Payment'}
                </h2>
                <button
                  onClick={handleClosePaymentModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Payment Form */}
              <form onSubmit={handleSubmitPayment}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Total Amount to Pay Summary */}
                  <div style={{
                    background: 'var(--bg-muted)',
                    padding: '1.25rem',
                    borderRadius: '0.75rem',
                    border: '2px solid var(--border-color)',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {texts.paymentModal?.paymentSummary || 'Payment Summary'}
                      </label>
                    </div>
                    
                    {/* Total Amount Due */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      paddingBottom: '0.5rem',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.paymentModal?.totalAmountDue || 'Total Amount Due'}:</span>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                        ₹{totalDueAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>

                    {/* Total Paid */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      paddingBottom: '0.5rem',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.paymentModal?.totalPaid || 'Total Paid'}:</span>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#059669' }}>
                        ₹{totalPaidAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>

                    {/* Outstanding Balance */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '0.5rem',
                      marginTop: '0.5rem',
                      borderTop: '2px solid var(--border-color)'
                    }}>
                      <span style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'var(--text-primary)' }}>{texts.paymentModal?.outstandingBalance || 'Outstanding Balance'}:</span>
                      <span style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: 'bold', 
                        color: '#dc2626'
                      }}>
                        ₹{outstandingBalanceBeforePayment.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>

                    {paymentForm.paymentType === 'FULL_SETTLEMENT' && (
                      <div style={{
                        padding: '0.75rem',
                        marginBottom: '0.75rem',
                        borderRadius: '0.5rem',
                        background: 'rgba(220, 38, 38, 0.08)',
                        color: '#b91c1c',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        {texts.paymentModal?.expectedSettlement || 'Expected settlement amount'}: ₹{outstandingBalanceBeforePayment.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </div>
                    )}

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.5rem',
                      paddingTop: '0.75rem',
                      marginTop: '0.5rem',
                      borderTop: '1px solid var(--border-color)'
                    }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {texts.paymentModal?.interestAllocation || 'Interest'}: ₹{allocationPreview.interest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {texts.paymentModal?.principalAllocation || 'Principal'}: ₹{allocationPreview.principal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#2563eb' }}>
                        {texts.paymentModal?.roundOffAmount || 'Round-off'}: ₹{allocationPreview.roundOff.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {paymentForm.paymentType === 'FULL_SETTLEMENT' && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '0.5rem'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.paymentModal?.roundOffAmount || 'Round-off Amount (auto)'}:</span>
                        <span style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: roundOffAmountValue > 0 ? '#2563eb' : '#6b7280'
                        }}>
                          ₹{roundOffAmountValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Amount Input */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      {texts.paymentModal?.paymentAmount || 'Payment Amount'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={paymentForm.amount}
                      onChange={handlePaymentFormChange}
                      placeholder={texts.paymentModal?.enterPaymentAmount || "Enter payment amount"}
                      required
                      min="0"
                      step="0.01"
                      max={paymentMaximum}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-surface)'
                      }}
                    />
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      marginTop: '0.25rem'
                    }}>
                      {texts.paymentModal?.maximum || 'Maximum'}: ₹{paymentMaximum.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>

                  {/* Payment Method and Reference Number in Same Row */}
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    {/* Payment Method Dropdown */}
                    <div style={{ flex: 1 }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: 'var(--text-primary)',
                        marginBottom: '0.5rem'
                      }}>
                        {texts.paymentModal?.paymentMethod || 'Payment Method'} <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        name="paymentMethod"
                        value={paymentForm.paymentMethod}
                        onChange={handlePaymentFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-surface)',
                          appearance: 'none'
                        }}
                      >
                        <option value="CASH">{texts.paymentModal?.cash || 'Cash'}</option>
                        <option value="ONLINE">Online</option>
                        <option value="CHEQUE">{texts.paymentModal?.cheque || 'Cheque'}</option>
                        <option value="UPI">{texts.paymentModal?.upi || 'UPI'}</option>
                        <option value="BANK_TRANSFER">{texts.paymentModal?.bankTransfer || 'Bank Transfer'}</option>
                      </select>
                    </div>

                    {/* Reference Number (Optional) */}
                    <div style={{ flex: 1 }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: 'var(--text-primary)',
                        marginBottom: '0.5rem'
                      }}>
                        {texts.paymentModal?.referenceNumber || 'Reference Number'} <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}></span>
                      </label>
                      <input
                        type="text"
                        name="referenceNumber"
                        value={paymentForm.referenceNumber}
                        onChange={handlePaymentFormChange}
                        placeholder={texts.paymentModal?.enterReference || "Enter reference/transaction number"}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-surface)'
                        }}
                      />
                    </div>
                  </div>

                  {/* Payment Type */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      {texts.paymentModal?.paymentType || 'Payment Type'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      name="paymentType"
                      value={paymentForm.paymentType}
                      onChange={handlePaymentFormChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-surface)',
                        appearance: 'none'
                      }}
                    >
                      <option value="REGULAR_PAYMENT">{texts.paymentModal?.regularPayment || 'Regular Payment'}</option>
                      <option value="INTEREST_ONLY">{texts.paymentModal?.interestOnly || 'Interest Only'}</option>
                      <option value="FULL_SETTLEMENT">{texts.paymentModal?.fullSettlement || 'Full Settlement'}</option>
                    </select>
                  </div>

                  {/* Optional note */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '0.5rem'
                    }}>
                      {texts.paymentModal?.description || 'Note'}
                    </label>
                    <input
                      type="text"
                      name="description"
                      value={paymentForm.description}
                      onChange={handlePaymentFormChange}
                      placeholder={texts.paymentModal?.enterDescription || 'Optional note'}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-surface)'
                      }}
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  marginTop: '2rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <button
                    type="button"
                    onClick={handleClosePaymentModal}
                    style={{
                      padding: '0.875rem 2rem',
                      background: 'var(--bg-muted)',
                      color: 'var(--text-primary)',
                      border: '2px solid var(--border-color)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'var(--row-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'var(--bg-muted)';
                    }}
                  >
                    {texts.paymentModal?.cancel || 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingPayment}
                    style={{
                      padding: '0.875rem 2rem',
                      background: isSubmittingPayment ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: isSubmittingPayment ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmittingPayment) {
                        e.target.style.background = '#2563eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmittingPayment) {
                        e.target.style.background = '#3b82f6';
                      }
                    }}
                  >
                    {isSubmittingPayment ? (texts.paymentModal?.recording || 'Recording...') : (texts.paymentModal?.recordPayment || 'Record Payment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanDetails;
